import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { resolveBrand } from "./vynkoBrand";

// Fase 1 of the Vynko launch plan -- the brand context every page reads to
// know "which brand am I" and get its presentational values (name,
// domain, canonical origin, support email, legal name). Reuses
// resolveBrand() from vynkoBrand.js rather than reimplementing a second
// resolver -- this file is ONLY the per-brand value table + React wiring.
//
// Deliberately a static table, not a fetch to
// GET /api/ventures/nfc/brand/public (which exists, see
// venture_registry_service.py, but has no consumer yet): the brand must
// resolve synchronously, before first paint, or a Vynko customer would see
// a flash of "RevOSDirector" while a network request is in flight -- the
// exact thing the "no Director branding visible, ever, even transiently"
// principle rules out. A live overlay on top of these defaults (for the
// owner to tweak tagline/palette without a redeploy) is a reasonable
// follow-up, not a blocker for launch.
//
// Reusability: adding venture #2 later is adding one more row to
// BRAND_TABLE and one more host to vynkoBrand.js's allowlist -- no new
// resolver, no new context.

const BRAND_TABLE = {
  vynko: {
    id: "vynko",
    name: "Vynko",
    domain: "getvynko.com",
    canonicalOrigin: "https://getvynko.com",
    tagline: "One chip. Any job. Reprogram it for free, forever.",
    tagline_es: "Un chip. Cualquier uso. Reprográmalo gratis, para siempre.",
    supportEmail: "info@getvynko.com",
    legalName: "Vynko",
    // Fase B (brand rescue) -- the fields this table was missing entirely
    // until now, which is the root cause of a Vynko page ever showing
    // RevOSDirector's own favicon/wordmark: this table only had text
    // fields, so nothing in the app could tell an <img>/<link rel="icon">
    // which asset belonged to which brand. faviconUrl is served by the
    // per-host index.html (ops/Caddyfile, Fase B.1) already, not read from
    // here at runtime -- kept here too so a future venture's own build
    // step / any in-app reference (e.g. VynkoNav.js) has one source of
    // truth instead of a second hardcoded path.
    //
    // 2026-09-23 quality audit (H15): ogImageUrl used to be null -- flagged
    // as "no real Open Graph share image yet". Turned out to be simpler
    // than expected: backend/services/vynko_shell_service.py (the real SSR
    // meta source social crawlers actually read, not this client-side
    // context) already referenced `${_VYNKO_ORIGIN}/og-image.jpg` as its
    // default/fallback image in multiple places -- the wiring was correct
    // the whole time, the file at that exact path just never existed
    // (frontend/public/og-image.jpg). Generated from the real catalog hero
    // photo (nfc-card-standard.jpg, center-cropped 1200x1200 -> 1200x630,
    // no upscaling, no fabricated content), now shipped at that path.
    logoUrl: "/vynko-favicon.svg",
    faviconUrl: "/vynko-favicon.svg",
    ogImageUrl: "/og-image.jpg",
  },
  revosdirector: {
    id: "revosdirector",
    name: "Revos Director",
    domain: "revosdirector.com",
    canonicalOrigin: "https://revosdirector.com",
    tagline: "The Director for every venture you run.",
    tagline_es: "El Director de todos tus proyectos.",
    // 2026-10-03: was support@revosdirector.com, an address nothing in
    // docs/ops/EMAIL_DNS_VERIFICATION.md routes anywhere. info@ is the
    // mailbox Cloudflare Email Routing verifiably forwards (docs/STATE.md
    // master board, row A3) and the one the Instant Report order
    // confirmation already prints as the trader's contact, so a visitor
    // who writes to it is read.
    supportEmail: "info@revosdirector.com",
    legalName: "Revos Director",
    logoUrl: null,
    faviconUrl: null,
    ogImageUrl: null,
  },
};

const VynkoBrandContext = createContext(BRAND_TABLE.revosdirector);

export function VynkoBrandProvider({ children, hostname }) {
  // Found by adversarial review (Fase 2): pathname used to be a prop,
  // defaulted from window.location.pathname ONCE at first render and
  // memoized -- App.js's real usage never passes it, and React Router's
  // client-side navigation doesn't change VynkoBrandProvider's own props,
  // so the memo never recomputed. A user who client-navigates (e.g. the
  // <Link to="/my-cards"> in NFCOrderConfirmationPage.js) from a
  // non-product path into a product route kept seeing the STALE brand.
  // useLocation() is reactive to every navigation by construction, which
  // is exactly what a memo keyed on a never-changing prop could not be.
  // This requires a Router ancestor -- true of every real render path
  // (App.js always wraps this in <Router>); tests wrap in MemoryRouter.
  const location = useLocation();
  const value = useMemo(() => {
    const host = hostname !== undefined ? hostname : typeof window !== "undefined" ? window.location.hostname : "";
    const brandId = resolveBrand(host, location.pathname);
    return BRAND_TABLE[brandId] || BRAND_TABLE.revosdirector;
  }, [hostname, location.pathname]);

  return <VynkoBrandContext.Provider value={value}>{children}</VynkoBrandContext.Provider>;
}

export function useBrand() {
  return useContext(VynkoBrandContext);
}
